'use client';
import React, { useEffect, useRef, useState } from 'react';

const indicators = [
  { label: 'Faturamento', value: 'R$ 48.290', change: '+12,4%', up: true },
  { label: 'Lucro Líquido', value: 'R$ 18.640', change: '+8,1%', up: true },
  { label: 'Despesas', value: 'R$ 29.650', change: '-3,2%', up: false },
  { label: 'Fluxo de Caixa', value: 'R$ 12.180', change: '+5,7%', up: true },
];

const topProducts = [
  { name: 'X-Burguer Especial', qty: 247, rev: 'R$ 4.940' },
  { name: 'Frango Grelhado', qty: 189, rev: 'R$ 3.402' },
  { name: 'Refrigerante 600ml', qty: 412, rev: 'R$ 2.472' },
  { name: 'Prato Executivo', qty: 134, rev: 'R$ 5.360' },
];

export default function DashboardSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const barHeights = [45, 60, 52, 75, 58, 82, 68, 88, 72, 95, 78, 85];

  return (
    <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-96 h-96 blob-primary opacity-30 rounded-full" />
      <div ref={ref} className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/20 bg-primary/5 text-xs font-bold uppercase tracking-widest text-primary mb-6">
            <span className="w-2 h-2 rounded-full bg-primary" />
            Dashboard
          </span>
          <h2 className="text-section-title font-black text-foreground mb-4">
            Visão completa do seu{' '}
            <span className="gradient-text">negócio</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Indicadores em tempo real, comparações mensais e alertas inteligentes para decisões mais rápidas.
          </p>
        </div>

        {/* Dashboard mockup */}
        <div className={`glass-card rounded-2xl overflow-hidden shadow-2xl transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Topbar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                <div className="w-3 h-3 rounded-full bg-blue-500/60" />
              </div>
              <span className="text-sm font-bold text-muted-foreground">Dashboard — Julho 2026</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs text-primary font-medium">Ao vivo</span>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: KPIs + Chart */}
            <div className="lg:col-span-2 space-y-6">
              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3">
                {indicators?.map((ind, i) => (
                  <div
                    key={ind?.label}
                    className={`bg-muted/50 rounded-xl p-4 border border-border transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: `${200 + i * 100}ms` }}
                  >
                    <p className="text-xs text-muted-foreground mb-1">{ind?.label}</p>
                    <p className="text-xl font-black text-foreground">{ind?.value}</p>
                    <span className={`text-xs font-bold ${ind?.up ? 'text-primary' : 'text-red-400'}`}>
                      {ind?.up ? '↑' : '↓'} {ind?.change} este mês
                    </span>
                  </div>
                ))}
              </div>

              {/* Bar chart */}
              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-bold text-foreground">Faturamento Mensal</p>
                  <span className="text-xs text-primary font-medium">2026</span>
                </div>
                <div className="flex items-end gap-1 h-32">
                  {barHeights?.map((h, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <div
                        className="w-full rounded-t-sm transition-all duration-700"
                        style={{
                          height: visible ? `${h}%` : '0%',
                          background: i === 11 ? 'var(--primary)' : `rgba(30,111,255,${0.2 + i * 0.05})`,
                          transitionDelay: `${300 + i * 50}ms`,
                        }}
                      />
                      <span className="text-[8px] text-muted-foreground hidden sm:block">{months?.[i]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Top products + alerts */}
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-xl p-4 border border-border">
                <p className="text-sm font-bold text-foreground mb-3">Produtos Mais Vendidos</p>
                <div className="space-y-3">
                  {topProducts?.map((p, i) => (
                    <div key={p?.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span>
                        <div>
                          <p className="text-xs font-medium text-foreground leading-tight">{p?.name}</p>
                          <p className="text-xs text-muted-foreground">{p?.qty} unid.</p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-primary">{p?.rev}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-400/5 rounded-xl p-4 border border-yellow-400/20">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-yellow-400">⚠️</span>
                  <p className="text-sm font-bold text-yellow-400">Estoque Baixo</p>
                </div>
                <div className="space-y-2">
                  {['Farinha de Trigo (2kg)', 'Azeite Extra Virgem']?.map((item) => (
                    <div key={item} className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{item}</span>
                      <span className="text-xs font-bold text-yellow-400">Crítico</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
                <p className="text-sm font-bold text-primary mb-2">💡 Insight IA</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Faturamento 12% acima da média. Considere ampliar o estoque de X-Burguer para o fim de semana.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}